import { useState } from "react";
import { Camera, KeyRound, ScanLine, Smartphone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import Scanner from "./Scanner";
import { StaffPinCard } from "../components/StaffPinCard";

const STEPS = [
  { Icon: Camera, title: "Open the phone camera", body: "Any phone. No app, no login." },
  { Icon: ScanLine, title: "Point it at the customer's pass", body: "Tap the link that pops up." },
  { Icon: KeyRound, title: "Enter the staff PIN", body: "The stamp, visit, points or reward is recorded automatically and their pass updates instantly." },
];

export default function ScannerHub() {
  const { activeOrg } = useAuth();
  const [scanning, setScanning] = useState(false);
  if (!activeOrg) return null;
  if (scanning) return <Scanner onBack={() => setScanning(false)} />;

  return (
    <div className="space-y-6" data-testid="scanner-hub">
      <PageHeader title="Scanning" subtitle="Staff scan a customer's pass with any phone and enter the staff PIN. That's the whole flow." />
      <StaffPinCard orgId={activeOrg.id} />

      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="rounded-2xl bg-white border border-[#0B0D10]/10 p-6 sm:p-7" data-testid="scanner-hub-how">
          <h3 className="font-display font-bold text-[17px] tracking-[-0.01em]">How staff record a visit</h3>
          <ol className="mt-5 grid sm:grid-cols-3 gap-5">
            {STEPS.map(({ Icon, title, body }, i) => (
              <li key={title} className="flex sm:block gap-4">
                <div className="flex items-center gap-3 sm:mb-3">
                  <span className="h-10 w-10 rounded-xl bg-[#0B0D10] text-[#C8FF4D] grid place-items-center shrink-0"><Icon size={17} strokeWidth={2.2} /></span>
                  <span className="hidden sm:inline font-mono text-[11px] text-[#0B0D10]/45">0{i + 1}</span>
                </div>
                <div>
                  <div className="font-semibold text-[14px] text-[#0B0D10]">{title}</div>
                  <p className="text-[13px] text-[#0B0D10]/60 mt-1 leading-relaxed">{body}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-6 pt-5 border-t border-[#0B0D10]/[0.06] text-[12.5px] text-[#0B0D10]/55 leading-relaxed">
            Customers who open their own pass link only see their balance. Nothing can be added without the PIN. Every scan can be undone within 3 minutes.
          </p>
        </div>

        <div className="rounded-2xl bg-[#0B0D10] text-white p-6" data-testid="scanner-hub-device">
          <div className="h-10 w-10 rounded-xl bg-white/10 grid place-items-center mb-4"><Smartphone size={17} className="text-[#C8FF4D]" /></div>
          <h4 className="font-display font-bold text-[16px] tracking-[-0.01em]">Scan on this device</h4>
          <p className="text-[13px] text-white/60 mt-1.5 leading-relaxed">Use this screen as the till scanner. You're already signed in, so no PIN is needed here.</p>
          <button onClick={() => setScanning(true)} data-testid="scanner-hub-open-scanner" className="mt-5 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[#C8FF4D] text-[#0B0D10] h-11 text-[13.5px] font-semibold hover:bg-[#b8ef3d] transition">
            <Camera size={14} strokeWidth={2.4} />Open scanner
          </button>
        </div>
      </div>
    </div>
  );
}
